import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AuthenticationResponseJSON } from '@simplewebauthn/browser';
import { adminFetch, setAccessTokenGetter, setAuthHandlers } from './apiClient';
import { ApiError, type AdminSession, type AdminUser, type LoginResponse } from './types';
import {
  AuthContext,
  type AuthContextValue,
  type AuthStatus,
  type AuthProviderProps,
} from './AuthContext';

interface RefreshResponseBody {
  readonly accessToken: string;
  readonly accessTokenExpiresAt: string;
  readonly absoluteExpiresAt: string;
}

interface MeResponse {
  readonly adminUser: AdminUser;
}

/**
 * Module-level single-flight guard for `/admin/auth/refresh`. Lives outside the
 * component so it survives React StrictMode's dev double-mount (and any remount):
 * the cold-start refresh runs in a `useEffect([])` that StrictMode invokes twice,
 * which previously fired two concurrent refreshes carrying the same HttpOnly
 * cookie. Because the refresh token is single-use + rotated server-side, the
 * second call could hit the just-revoked token and log the admin out. Collapsing
 * concurrent refreshes (cold-start ×2, or a cold-start racing a reactive 401
 * retry) into ONE network call removes that race at the source and stops the
 * refresh-token chain from forking. (Server-side rotation grace — see
 * admin-auth.ts — is the load-bearing fix that also covers multi-tab.)
 */
let inFlightRefresh: Promise<void> | null = null;

/**
 * F06 — concrete AuthProvider. Exported separately from `AuthContext.tsx` so
 * the context object can live in a component-only file (avoids
 * `react-refresh/only-export-components`).
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [status, setStatus] = useState<AuthStatus>('unknown');

  // Mirror the access token into a ref so the apiClient closure always reads
  // the latest value without depending on the React render cycle.
  const accessTokenRef = useRef<string | null>(null);
  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  const refresh = useCallback(async (): Promise<void> => {
    // Single-flight: a concurrent refresh already in progress is reused rather
    // than issuing a second /admin/auth/refresh with the same (single-use) cookie.
    if (inFlightRefresh) {
      await inFlightRefresh;
      return;
    }
    inFlightRefresh = (async () => {
      const body = await adminFetch<RefreshResponseBody>('/admin/auth/refresh', {
        method: 'POST',
      });
      setAccessToken(body.accessToken);
      // Keep the ref in sync immediately so the retried fetch (in apiClient)
      // sees the new token on the same tick.
      accessTokenRef.current = body.accessToken;
    })().finally(() => {
      inFlightRefresh = null;
    });
    await inFlightRefresh;
  }, []);

  const refreshMe = useCallback(async (): Promise<AdminUser | null> => {
    try {
      const body = await adminFetch<MeResponse>('/admin/me');
      setAdminUser(body.adminUser);
      return body.adminUser;
    } catch {
      setAdminUser(null);
      return null;
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await adminFetch('/admin/auth/logout', { method: 'POST' });
    } catch {
      // Best-effort — even if the server rejects (e.g. no cookie), clear state.
    }
    setAccessToken(null);
    accessTokenRef.current = null;
    setAdminUser(null);
    setStatus('unauthenticated');
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResponse> => {
    const body = await adminFetch<LoginResponse>('/admin/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    return body;
  }, []);

  const confirmTotp = useCallback(
    async (challengeToken: string, code: string): Promise<AdminSession> => {
      const session = await adminFetch<AdminSession>('/admin/auth/login/totp', {
        method: 'POST',
        body: { totpChallengeToken: challengeToken, code },
      });
      setAccessToken(session.accessToken);
      accessTokenRef.current = session.accessToken;
      setAdminUser(session.adminUser);
      setStatus('authenticated');
      return session;
    },
    [],
  );

  const confirmSecurityKey = useCallback(
    async (
      challengeToken: string,
      challengeId: string,
      assertion: AuthenticationResponseJSON,
    ): Promise<AdminSession> => {
      const session = await adminFetch<AdminSession>('/admin/auth/login/security-key', {
        method: 'POST',
        body: { totpChallengeToken: challengeToken, challengeId, assertion: { ...assertion } },
      });
      setAccessToken(session.accessToken);
      accessTokenRef.current = session.accessToken;
      setAdminUser(session.adminUser);
      setStatus('authenticated');
      return session;
    },
    [],
  );

  const confirmEnrollment = useCallback(
    async (enrollmentToken: string, code: string): Promise<AdminSession> => {
      const session = await adminFetch<AdminSession>('/admin/auth/totp/confirm', {
        method: 'POST',
        body: { enrollmentToken, code },
      });
      setAccessToken(session.accessToken);
      accessTokenRef.current = session.accessToken;
      setAdminUser(session.adminUser);
      setStatus('authenticated');
      return session;
    },
    [],
  );

  // Wire the apiClient closure to live React state once at provider mount.
  useEffect(() => {
    setAccessTokenGetter(() => accessTokenRef.current);
    setAuthHandlers({
      refresh,
      onAuthFailure: () => {
        setAccessToken(null);
        accessTokenRef.current = null;
        setAdminUser(null);
        setStatus('unauthenticated');
      },
    });
  }, [refresh]);

  // Dev/test-only escape hatch for Playwright E2E specs — see the matching
  // hook in `apps/portal/src/auth/AuthProvider.tsx` for the rationale. The
  // F17 + F20 specs use this to drive admin-side flows (force-transition,
  // dispute resolve) without walking the email+TOTP login flow in the UI.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const w = window as unknown as {
      __pulseAdminSetAccessTokenForTesting?: (token: string) => Promise<void>;
    };
    w.__pulseAdminSetAccessTokenForTesting = async (token: string) => {
      setAccessToken(token);
      accessTokenRef.current = token;
      // Hydrate /admin/me FIRST so when status flips to 'authenticated' the
      // adminUser is already populated. ProtectedRoute checks both `status`
      // AND `adminUser` — flipping status before adminUser is set would
      // briefly redirect to /login.
      await refreshMe();
      setStatus('authenticated');
    };
    return () => {
      delete w.__pulseAdminSetAccessTokenForTesting;
    };
  }, [refreshMe]);

  // Cold-start restore: attempt refresh, then hydrate /me.
  useEffect(() => {
    // DEV-only: Playwright sets `window.__pulseTestingMode = true` via
    // addInitScript BEFORE any React code runs. When present, skip the
    // cold-start refresh entirely and wait for the testing hook to inject
    // a token.
    if (
      import.meta.env.DEV &&
      (window as unknown as { __pulseTestingMode?: boolean }).__pulseTestingMode === true
    ) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await refresh();
        if (cancelled) return;
      } catch (err) {
        if (cancelled) return;
        // No valid refresh cookie — treat as anonymous.
        if (err instanceof ApiError && err.status === 401) {
          setStatus('unauthenticated');
          return;
        }
        setStatus('unauthenticated');
        return;
      }
      const me = await refreshMe();
      if (cancelled) return;
      setStatus(me ? 'authenticated' : 'unauthenticated');
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      adminUser,
      accessToken,
      status,
      login,
      confirmTotp,
      confirmSecurityKey,
      confirmEnrollment,
      logout,
      refresh,
      refreshMe,
    }),
    [
      adminUser,
      accessToken,
      status,
      login,
      confirmTotp,
      confirmSecurityKey,
      confirmEnrollment,
      logout,
      refresh,
      refreshMe,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
